import { FC, useMemo, useState, ReactNode } from "react"
import cx from "classnames"
import Table from "@/components/Table"
import DeleteDNCModal from "../DeleteDNCModal"
import AddEditDNCModal from "../AddEditDNCModal"
import { DATE_FORMAT_OPTIONS } from "@/constants"
import { useOrganizationPermissions } from "@/hooks/useOrganizationPermissions"
import { RiDeleteBin6Line as BinIcon } from "react-icons/ri"
import { MdOutlineModeEdit as EditIcon } from "react-icons/md"
import type { AdminDNC } from "@/types/entities/dnc"
import type { TablePaginationProps } from "@/redux/services/types"
import type { ColumnsType } from "antd/es/table"

type Props = {
  className?: string
  dncs: AdminDNC[]
} & TablePaginationProps<TableDataType>

type TableDataType = {
  key: string
  value: string
  reason: string
  createdAt: string
}

const UserDNCTable: FC<Props> = ({
  className,
  dncs,
  onRowSelect,
  ...rest
}: Props): ReactNode => {
  const [editedUserId, setEditedUserId] = useState("")
  const [deletedUserId, setDeletedUserId] = useState("")
  const { hasEditPermission } = useOrganizationPermissions({ page: "dnc" })
  const dataSource = useMemo<TableDataType[]>(
    () =>
      dncs.map(({ _id, value, reason, createdAt }) => ({
        key: _id,
        value,
        reason,
        createdAt: new Date(createdAt).toLocaleDateString(
          "en-US",
          DATE_FORMAT_OPTIONS,
        ),
      })),
    [dncs],
  )
  const columns = useMemo<ColumnsType<TableDataType>>(() => {
    const cols: ColumnsType<TableDataType> = [
      {
        key: "value",
        dataIndex: "value",
        title: "Contact Name/Address",
        ellipsis: true,
      },
      {
        key: "reason",
        dataIndex: "reason",
        title: "Reason",
        ellipsis: true,
      },
      {
        key: "createdAt",
        dataIndex: "createdAt",
        title: "Created Date",
        ellipsis: true,
      },
    ]

    if (hasEditPermission) {
      cols.push({
        key: "actions",
        title: "Actions",
        width: "150px",
        align: "center",
        render: (_: any, record: TableDataType) => (
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setEditedUserId(record.key)}
              className="text-primary hover:text-primary-dark transition-colors h-fit"
            >
              <EditIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setDeletedUserId(record.key)}
              className="text-danger hover:text-danger-dark transition-colors h-fit"
            >
              <BinIcon className="w-5 h-5" />
            </button>
          </div>
        ),
      })
    }

    return cols
  }, [hasEditPermission])

  return (
    <>
      <Table
        dataSource={dataSource}
        columns={columns}
        rootClassName="w-full"
        className={cx("", className)}
        showSizeChanger
        pagination={{
          current: Math.floor(rest.page / rest.pageSize) + 1,
          total: rest.total,
          onChange: (page) => rest.onPageChange?.((page - 1) * rest.pageSize),
        }}
        {...rest}
      ></Table>
      <AddEditDNCModal
        data={dncs.find(({ _id }) => _id === editedUserId)}
        open={!!editedUserId}
        onClose={() => setEditedUserId("")}
      />
      <DeleteDNCModal
        open={!!deletedUserId}
        data={dncs.find(({ _id }) => _id === deletedUserId)}
        onClose={() => setDeletedUserId("")}
      />
    </>
  )
}

export default UserDNCTable
